(() => {
  const SECTION_CLASS = "highlighted-products-section";
  const ROOT_SELECTOR = ".highlighted-products__products--mobile-slider";
  // The scrollbar sits outside the slider root (a sibling in the section
  // layout), so it is resolved from the section, not from the root.
  const SCROLLBAR_SELECTOR = ".highlighted-products__scrollbar";
  // Desktop keeps the products as a static single column next to the sticky promo,
  // so the slider only exists below $lg — mirrors the CSS grid/slider switch.
  const MOBILE_QUERY = "(max-width: 989.98px)";
  // Same 12/24 slide gap the shared sliders use (global.js), matching the grid gap.
  const SPACE_BETWEEN = 12;
  const SPACE_BETWEEN_WIDE = 24;

  // Keyed by section id, not by element: the theme editor swaps in fresh markup
  // on a settings change, so an element-keyed registry never finds the previous
  // instance and leaks its MediaQueryList listener (rule D). Mirrors the
  // section-keyed cleanup registry in initSectionSliderLayout (global.js).
  const instances = new Map();

  class MobileProductsSlider {
    constructor(section, root) {
      this.section = section;
      this.root = root;
      this.track = root.querySelector(".highlighted-products__track");
      if (!this.track || typeof Swiper === "undefined") return;

      this.slidesPerView = Number(root.dataset.mobileCardsPerRow) || 1;
      this.mobile = window.matchMedia(MOBILE_QUERY);
      this.swiper = null;
      this._onBreakpointChange = this._onBreakpointChange.bind(this);

      this.mobile.addEventListener("change", this._onBreakpointChange);
      this._sync();
    }

    _onBreakpointChange() {
      this._sync();
    }

    _sync() {
      if (this.mobile.matches) {
        this._createSwiper();
      } else {
        this._destroySwiper();
      }
    }

    _createSwiper() {
      if (this.swiper) return;

      // Idempotent: the markup already ships these when the slider is on, but
      // re-assert them so a re-init after teardown never sees 0 slides (rule P).
      this.track.classList.add("swiper-wrapper");
      Array.from(this.track.children).forEach((slide) =>
        slide.classList.add("swiper-slide")
      );

      const scrollbarEl = this.section.querySelector(SCROLLBAR_SELECTOR);

      this.swiper = new Swiper(this.root, {
        slidesPerView: this.slidesPerView,
        spaceBetween: SPACE_BETWEEN,
        speed: 600,
        loop: false,
        watchSlideProgress: true,
        mousewheel: { forceToAxis: true },
        a11y: { scrollOnFocus: false },
        // Only the gap changes across mobile widths: slidesPerView stays at the
        // merchant's Mobile cards per row, matching the CSS grid above $lg.
        breakpoints: {
          576: { spaceBetween: SPACE_BETWEEN_WIDE },
        },
        scrollbar: scrollbarEl
          ? {
              el: scrollbarEl,
              grabCursor: true,
              draggable: true,
              hide: false,
            }
          : false,
      });
    }

    _destroySwiper() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    destroy() {
      this._destroySwiper();
      if (this.mobile) {
        this.mobile.removeEventListener("change", this._onBreakpointChange);
      }
    }
  }

  const keyFor = (section) => section.id || section;

  const destroyInstance = (key) => {
    const existing = instances.get(key);
    if (!existing) return;
    existing.destroy();
    instances.delete(key);
  };

  const initSection = (section) => {
    if (!section || !section.classList?.contains(SECTION_CLASS)) return;

    const key = keyFor(section);
    // Tear down before the root lookup so switching Enable slider on mobile off
    // — where the new markup has no slider root at all — also cleans up.
    destroyInstance(key);

    const root = section.querySelector(ROOT_SELECTOR);
    if (!root) return;

    instances.set(key, new MobileProductsSlider(section, root));
  };

  initSection(document.currentScript.parentElement);

  if (!window.__themeHighlightedProductsEvents) {
    window.__themeHighlightedProductsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const section = event.target;
      if (!section || typeof section.querySelector !== "function") return;
      destroyInstance(keyFor(section));
    });
  }
})();
